import type { PhotoDetail } from '@/types/photo';
import { AUTH_MODE } from '@/services/api';
import type { PhotoSearchOptions } from '@/services/photoApi';

const DB_NAME = 'bongnas_photos';
const DB_VERSION = 1;
const STORE_BLOB = 'photo_blob';
const META_KEY = 'bongnas_photo_meta_v2';

// ── In-memory cache ──
let _metaCache: PhotoDetail[] | null = null;

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_BLOB)) {
        db.createObjectStore(STORE_BLOB, { keyPath: 'id' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

// ── Seed data: all 15 photos ──
const SEED_PHOTOS: PhotoDetail[] = [
  {
    id: 'p1', albumId: '1',
    image: 'https://readdy.ai/api/search-image?query=Family%20building%20snowman%20together%20in%20front%20of%20wooden%20cabin%20during%20winter%2C%20happy%20parents%20and%20children%20in%20warm%20coats%2C%20snow%20covered%20trees%20in%20background%2C%20soft%20natural%20winter%20light%2C%20cream%20and%20white%20color%20palette%2C%20joyful%20lifestyle%20photography%20with%20candid%20expressions%20and%20gentle%20shadows%2C%20simple%20clean%20composition&width=600&height=600&seq=photo-winter-01&orientation=squarish',
    caption: '눈사람 만들기', shotDate: '2025.12.25', uploadDate: '2025.12.25',
    uploaderName: '엄마', uploaderEmail: 'mom@family.com',
    originalFilename: 'DSC_0001.jpg', fileSize: 4521984, width: 4000, height: 3000, fileType: 'image/jpeg',
  },
  {
    id: 'p2', albumId: '1',
    image: 'https://readdy.ai/api/search-image?query=Cozy%20family%20sitting%20around%20fireplace%20inside%20warm%20cabin%2C%20drinking%20hot%20chocolate%2C%20wooden%20interior%20with%20soft%20glow%20from%20fire%2C%20parents%20and%20two%20children%20wrapped%20in%20cream%20blankets%2C%20warm%20beige%20and%20amber%20color%20palette%2C%20intimate%20lifestyle%20photography%20with%20soft%20focus%20and%20cozy%20atmosphere%2C%20simple%20clean%20composition&width=600&height=600&seq=photo-winter-02&orientation=squarish',
    caption: '따뜻한 벽난로 앞에서', shotDate: '2025.12.26', uploadDate: '2025.12.26',
    uploaderName: '아빠', uploaderEmail: 'dad@family.com',
    originalFilename: 'DSC_0015.jpg', fileSize: 3876543, width: 4000, height: 3000, fileType: 'image/jpeg',
  },
  {
    id: 'p3', albumId: '1',
    image: 'https://readdy.ai/api/search-image?query=Children%20sledding%20down%20gentle%20snowy%20hill%20with%20parents%20watching%20and%20laughing%2C%20winter%20forest%20background%20with%20bare%20trees%20and%20soft%20snowfall%2C%20bright%20overcast%20winter%20light%2C%20cream%20and%20white%20color%20palette%2C%20action%20lifestyle%20photography%20capturing%20joy%20and%20movement%2C%20simple%20clean%20composition&width=600&height=600&seq=photo-winter-03&orientation=squarish',
    caption: '신나는 썰매 타기', shotDate: '2025.12.27', uploadDate: '2025.12.27',
    uploaderName: '엄마', uploaderEmail: 'mom@family.com',
    originalFilename: 'IMG_0234.jpg', fileSize: 5123456, width: 4032, height: 3024, fileType: 'image/jpeg',
  },
  {
    id: 'p4', albumId: '2',
    image: 'https://readdy.ai/api/search-image?query=Cute%20one%20year%20old%20baby%20boy%20in%20cream%20colored%20traditional%20Korean%20first%20birthday%20outfit%20sitting%20at%20small%20table%20with%20birthday%20cake%2C%20soft%20natural%20light%20from%20window%2C%20minimalist%20beige%20and%20cream%20decorations%2C%20gentle%20smiling%20expression%2C%20editorial%20baby%20portrait%20photography%20with%20soft%20bokeh%20background%2C%20simple%20clean%20composition&width=600&height=600&seq=photo-birthday-01&orientation=squarish',
    caption: '돌잡이 하는 지호', shotDate: '2026.03.15', uploadDate: '2026.03.15',
    uploaderName: '엄마', uploaderEmail: 'mom@family.com',
    originalFilename: 'DSC_0150.jpg', fileSize: 3891200, width: 4000, height: 3000, fileType: 'image/jpeg',
  },
  {
    id: 'p5', albumId: '2',
    image: 'https://readdy.ai/api/search-image?query=Family%20gathered%20around%20baby%20blowing%20out%20first%20birthday%20candle%2C%20grandparents%20and%20parents%20surrounding%20small%20table%2C%20warm%20indoor%20lighting%20with%20candlelight%20glow%2C%20cream%20and%20beige%20color%20palette%2C%20emotional%20family%20celebration%20photography%20with%20genuine%20expressions%2C%20simple%20clean%20composition&width=600&height=600&seq=photo-birthday-02&orientation=squarish',
    caption: '생일 촛불 끄기', shotDate: '2026.03.15', uploadDate: '2026.03.15',
    uploaderName: '아빠', uploaderEmail: 'dad@family.com',
    originalFilename: 'DSC_0165.jpg', fileSize: 4235000, width: 4000, height: 3000, fileType: 'image/jpeg',
  },
  {
    id: 'p6', albumId: '2',
    image: 'https://readdy.ai/api/search-image?query=Baby%20boy%20smiling%20with%20cake%20frosting%20on%20his%20nose%20and%20cheeks%2C%20close%20up%20portrait%2C%20cream%20colored%20high%20chair%2C%20soft%20natural%20daylight%2C%20beige%20and%20cream%20color%20palette%2C%20adorable%20candid%20baby%20photography%20with%20sparkling%20eyes%20and%20messy%20cake%20moment%2C%20simple%20clean%20background&width=600&height=600&seq=photo-birthday-03&orientation=squarish',
    caption: '케이크 먹방', shotDate: '2026.03.15', uploadDate: '2026.03.15',
    uploaderName: '엄마', uploaderEmail: 'mom@family.com',
    originalFilename: 'IMG_0456.jpg', fileSize: 3456000, width: 3024, height: 4032, fileType: 'image/jpeg',
  },
  {
    id: 'p7', albumId: '3',
    image: 'https://readdy.ai/api/search-image?query=Elderly%20Korean%20grandmother%20sitting%20on%20traditional%20wooden%20floor%20with%20grandchildren%20gathered%20around%2C%20warm%20afternoon%20sunlight%20through%20paper%20doors%2C%20traditional%20Korean%20house%20interior%20with%20wooden%20furniture%2C%20cream%20and%20beige%20color%20palette%2C%20heartwarming%20family%20portrait%20photography%20with%20natural%20expressions%2C%20simple%20clean%20composition&width=600&height=600&seq=photo-grandma-01&orientation=squarish',
    caption: '할머니와 함께', shotDate: '2026.05.08', uploadDate: '2026.05.08',
    uploaderName: '엄마', uploaderEmail: 'mom@family.com',
    originalFilename: 'DSC_0201.jpg', fileSize: 3891200, width: 4000, height: 3000, fileType: 'image/jpeg',
  },
  {
    id: 'p8', albumId: '3',
    image: 'https://readdy.ai/api/search-image?query=Family%20cooking%20traditional%20Korean%20food%20together%20in%20rustic%20kitchen%2C%20grandmother%20teaching%20daughter%20and%20grandchildren%2C%20steaming%20pots%20and%20fresh%20ingredients%20on%20counter%2C%20warm%20indoor%20lighting%2C%20beige%20and%20cream%20color%20palette%2C%20lifestyle%20photography%20capturing%20family%20bonding%20moment%2C%20simple%20clean%20composition&width=600&height=600&seq=photo-grandma-02&orientation=squarish',
    caption: '함께 만든 손만두', shotDate: '2026.05.08', uploadDate: '2026.05.08',
    uploaderName: '아빠', uploaderEmail: 'dad@family.com',
    originalFilename: 'DSC_0210.jpg', fileSize: 4156000, width: 4000, height: 3000, fileType: 'image/jpeg',
  },
  {
    id: 'p9', albumId: '4',
    image: 'https://readdy.ai/api/search-image?query=Family%20enjoying%20picnic%20under%20cherry%20blossom%20trees%20in%20spring%2C%20sitting%20on%20cream%20colored%20blanket%20with%20picnic%20basket%2C%20children%20running%20in%20background%2C%20soft%20pink%20petals%20falling%2C%20warm%20spring%20sunlight%2C%20beige%20and%20soft%20pink%20color%20palette%2C%20joyful%20lifestyle%20photography%2C%20simple%20clean%20composition&width=600&height=600&seq=photo-picnic-01&orientation=squarish',
    caption: '벚꽃 아래 피크닉', shotDate: '2026.04.15', uploadDate: '2026.04.15',
    uploaderName: '엄마', uploaderEmail: 'mom@family.com',
    originalFilename: 'DSC_0301.jpg', fileSize: 4523000, width: 4032, height: 3024, fileType: 'image/jpeg',
  },
  {
    id: 'p10', albumId: '4',
    image: 'https://readdy.ai/api/search-image?query=Children%20flying%20colorful%20kite%20in%20open%20grassy%20park%20on%20sunny%20day%2C%20parents%20watching%20from%20distance%2C%20clear%20blue%20sky%20with%20fluffy%20clouds%2C%20green%20grass%20field%2C%20cream%20and%20soft%20pastel%20color%20palette%2C%20dynamic%20outdoor%20lifestyle%20photography%2C%20simple%20clean%20composition&width=600&height=600&seq=photo-picnic-02&orientation=squarish',
    caption: '연 날리기', shotDate: '2026.04.20', uploadDate: '2026.04.20',
    uploaderName: '아빠', uploaderEmail: 'dad@family.com',
    originalFilename: 'DSC_0315.jpg', fileSize: 3789000, width: 4000, height: 3000, fileType: 'image/jpeg',
  },
  {
    id: 'p11', albumId: '5',
    image: 'https://readdy.ai/api/search-image?query=Young%20Korean%20girl%20in%20graduation%20cap%20and%20gown%20holding%20diploma%20with%20proud%20smile%2C%20standing%20in%20front%20of%20school%20building%2C%20cream%20colored%20flowers%20bouquet%2C%20bright%20sunny%20day%2C%20beige%20and%20cream%20color%20palette%2C%20editorial%20portrait%20photography%20with%20natural%20light%2C%20simple%20clean%20composition&width=600&height=600&seq=photo-grad-01&orientation=squarish',
    caption: '졸업장을 든 수아', shotDate: '2026.02.28', uploadDate: '2026.02.28',
    uploaderName: '엄마', uploaderEmail: 'mom@family.com',
    originalFilename: 'DSC_0401.jpg', fileSize: 4125000, width: 4000, height: 3000, fileType: 'image/jpeg',
  },
  {
    id: 'p12', albumId: '5',
    image: 'https://readdy.ai/api/search-image?query=Family%20group%20photo%20at%20graduation%20ceremony%20with%20graduate%20girl%20in%20center%20holding%20flowers%2C%20parents%20and%20younger%20sibling%20on%20each%20side%2C%20indoor%20auditorium%20with%20warm%20lighting%2C%20cream%20and%20beige%20color%20palette%2C%20celebratory%20family%20portrait%20photography%2C%20simple%20clean%20composition&width=600&height=600&seq=photo-grad-02&orientation=squarish',
    caption: '가족 기념사진', shotDate: '2026.02.28', uploadDate: '2026.02.28',
    uploaderName: '아빠', uploaderEmail: 'dad@family.com',
    originalFilename: 'DSC_0410.jpg', fileSize: 3987000, width: 4000, height: 3000, fileType: 'image/jpeg',
  },
  {
    id: 'p13', albumId: '6',
    image: 'https://readdy.ai/api/search-image?query=Family%20walking%20along%20Jeju%20Island%20coastal%20trail%20with%20dramatic%20basalt%20cliffs%20and%20blue%20ocean%2C%20windy%20summer%20day%2C%20parents%20holding%20children%20hands%2C%20bright%20sunlight%2C%20cream%20and%20soft%20blue%20color%20palette%2C%20travel%20lifestyle%20photography%20capturing%20adventure%2C%20simple%20clean%20composition&width=600&height=600&seq=photo-jeju-01&orientation=squarish',
    caption: '제주 올레길 산책', shotDate: '2025.08.12', uploadDate: '2025.08.12',
    uploaderName: '엄마', uploaderEmail: 'mom@family.com',
    originalFilename: 'DSC_0501.jpg', fileSize: 4789000, width: 4000, height: 3000, fileType: 'image/jpeg',
  },
  {
    id: 'p14', albumId: '6',
    image: 'https://readdy.ai/api/search-image?query=Children%20playing%20on%20Jeju%20sandy%20beach%20with%20clear%20turquoise%20water%2C%20building%20sandcastles%2C%20parents%20relaxing%20under%20cream%20colored%20umbrella%2C%20bright%20summer%20sun%2C%20beige%20and%20soft%20turquoise%20color%20palette%2C%20vacation%20lifestyle%20photography%20with%20playful%20energy%2C%20simple%20clean%20composition&width=600&height=600&seq=photo-jeju-02&orientation=squarish',
    caption: '협재 해변에서', shotDate: '2025.08.14', uploadDate: '2025.08.14',
    uploaderName: '아빠', uploaderEmail: 'dad@family.com',
    originalFilename: 'DSC_0515.jpg', fileSize: 5123000, width: 4032, height: 3024, fileType: 'image/jpeg',
  },
  {
    id: 'p15', albumId: '6',
    image: 'https://readdy.ai/api/search-image?query=Family%20enjoying%20sunset%20dinner%20at%20Jeju%20seaside%20restaurant%20with%20ocean%20view%2C%20fresh%20seafood%20on%20table%2C%20golden%20hour%20warm%20light%2C%20cream%20and%20amber%20color%20palette%2C%20intimate%20travel%20dining%20photography%2C%20simple%20clean%20composition&width=600&height=600&seq=photo-jeju-03&orientation=squarish',
    caption: '석양과 저녁 식사', shotDate: '2025.08.15', uploadDate: '2025.08.15',
    uploaderName: '엄마', uploaderEmail: 'mom@family.com',
    originalFilename: 'DSC_0520.jpg', fileSize: 4456000, width: 4000, height: 3000, fileType: 'image/jpeg',
  },
];

// ── localStorage metadata helpers ──

function readMetaStorage(): PhotoDetail[] {
  if (AUTH_MODE !== 'mock') return [];
  try {
    const raw = localStorage.getItem(META_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    }
  } catch { /* ignore corrupted data */ }
  return [];
}

function writeMetaStorage(photos: PhotoDetail[]): void {
  if (AUTH_MODE !== 'mock') return;
  try {
    localStorage.setItem(META_KEY, JSON.stringify(photos));
  } catch { /* ignore quota exceeded */ }
}

/** Ensure metadata is loaded (memory -> localStorage -> seed) */
function ensureMeta(): PhotoDetail[] {
  if (_metaCache) return _metaCache;
  const stored = readMetaStorage();
  if (stored.length > 0) {
    _metaCache = stored;
    return _metaCache;
  }
  _metaCache = [...SEED_PHOTOS];
  writeMetaStorage(_metaCache);
  return _metaCache;
}

// ── Blob helpers (IndexedDB) ──

/** Pre-fetch image blobs and store in IndexedDB */
async function seedBlobs(): Promise<void> {
  if (AUTH_MODE !== 'mock') return;

  const photos = ensureMeta();
  let db: IDBDatabase | null = null;

  try {
    db = await openDB();
  } catch {
    return; // IndexedDB unavailable
  }

  // Find missing blobs
  const missingIds: string[] = [];
  for (const photo of photos) {
    const hasBlob = await new Promise<boolean>((resolve) => {
      const req = db!.transaction(STORE_BLOB, 'readonly').objectStore(STORE_BLOB).count(photo.id);
      req.onsuccess = () => resolve(req.result > 0);
      req.onerror = () => resolve(false);
    });
    if (!hasBlob) missingIds.push(photo.id);
  }
  db.close();

  if (missingIds.length === 0) return;

  // Fetch blobs OUTSIDE of any transaction
  const blobs: Record<string, Blob> = {};
  for (const id of missingIds) {
    const photo = photos.find((p) => p.id === id);
    if (!photo) continue;
    try {
      const response = await fetch(photo.image);
      if (response.ok) {
        blobs[id] = await response.blob();
      }
    } catch {
      // fetch failed, skip this blob
    }
  }

  // Store all blobs in ONE synchronous transaction (no await inside)
  try {
    const db2 = await openDB();
    const tx = db2.transaction(STORE_BLOB, 'readwrite');
    for (const id of missingIds) {
      if (blobs[id]) {
        tx.objectStore(STORE_BLOB).put({ id, blob: blobs[id] });
      }
    }
    await new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => { db2.close(); resolve(); };
      tx.onerror = () => { db2.close(); reject(tx.error); };
    });
  } catch {
    // IndexedDB write failed — blobs will be fetched on-demand
  }
}

async function getBlobFromDB(id: string): Promise<Blob | undefined> {
  try {
    const db = await openDB();
    const result = await new Promise<{ id: string; blob: Blob } | undefined>((resolve) => {
      const req = db.transaction(STORE_BLOB, 'readonly').objectStore(STORE_BLOB).get(id);
      req.onsuccess = () => resolve(req.result || undefined);
      req.onerror = () => resolve(undefined);
    });
    db.close();
    return result?.blob;
  } catch {
    return undefined;
  }
}

async function deleteBlobFromDB(id: string): Promise<void> {
  try {
    const db = await openDB();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_BLOB, 'readwrite');
      tx.objectStore(STORE_BLOB).delete(id);
      tx.oncomplete = () => { db.close(); resolve(); };
      tx.onerror = () => { db.close(); reject(tx.error); };
    });
  } catch { /* ignore */ }
}

// ── Public API ──

export async function getAllPhotos(): Promise<PhotoDetail[]> {
  if (AUTH_MODE !== 'mock') return [];
  const photos = ensureMeta();
  // Fire-and-forget blob seeding
  seedBlobs().catch(() => {});
  return [...photos];
}

export async function getPhotosByAlbumId(albumId: string): Promise<PhotoDetail[]> {
  const all = await getAllPhotos();
  return all.filter((p) => p.albumId === albumId);
}

export async function getPhotoById(id: string): Promise<PhotoDetail | undefined> {
  if (AUTH_MODE !== 'mock') return undefined;
  const photos = ensureMeta();
  seedBlobs().catch(() => {});
  return photos.find((p) => p.id === id);
}

export async function getPhotosByIds(ids: string[]): Promise<PhotoDetail[]> {
  const all = await getAllPhotos();
  return all.filter((p) => ids.includes(p.id));
}

/** Get photo blob: tries IndexedDB first, falls back to fetching from URL */
export async function getPhotoBlob(id: string): Promise<{ blob: Blob; meta: PhotoDetail } | null> {
  const meta = await getPhotoById(id);
  if (!meta) return null;

  // Try IndexedDB first
  const storedBlob = await getBlobFromDB(id);
  if (storedBlob) {
    return { blob: storedBlob, meta };
  }

  // Fallback: fetch from image URL
  try {
    const response = await fetch(meta.image);
    if (response.ok) {
      const blob = await response.blob();
      // Store for next time
      try {
        const db = await openDB();
        const tx = db.transaction(STORE_BLOB, 'readwrite');
        tx.objectStore(STORE_BLOB).put({ id, blob });
        await new Promise<void>((resolve, reject) => {
          tx.oncomplete = () => { db.close(); resolve(); };
          tx.onerror = () => { db.close(); reject(tx.error); };
        });
      } catch { /* ignore store failure */ }
      return { blob, meta };
    }
  } catch { /* fetch failed */ }

  return null;
}

export async function updatePhotoCaption(id: string, caption: string): Promise<PhotoDetail | undefined> {
  if (AUTH_MODE !== 'mock') return undefined;
  const photos = ensureMeta();
  const index = photos.findIndex((p) => p.id === id);
  if (index === -1) return undefined;

  const updated = { ...photos[index], caption: caption.trim() };
  photos[index] = updated;
  writeMetaStorage(photos);
  return updated;
}

export async function deletePhoto(id: string): Promise<boolean> {
  if (AUTH_MODE !== 'mock') return false;
  const photos = ensureMeta();
  const filtered = photos.filter((p) => p.id !== id);
  if (filtered.length === photos.length) return false;

  _metaCache = filtered;
  writeMetaStorage(filtered);
  deleteBlobFromDB(id);
  return true;
}

/** Alias for photoService compatibility */
export async function downloadPhoto(id: string): Promise<{ blob: Blob; filename: string } | null> {
  const result = await getPhotoBlob(id);
  if (!result) return null;
  return {
    blob: result.blob,
    filename: result.meta.originalFilename || `photo_${id}.jpg`,
  };
}

/** Get recent photos sorted by shotDate descending */
export async function getRecentPhotos(count: number): Promise<PhotoDetail[]> {
  const all = await getAllPhotos();
  return [...all].sort((a, b) => b.shotDate.localeCompare(a.shotDate)).slice(0, count);
}

export async function searchPhotos(options: PhotoSearchOptions = {}): Promise<PhotoDetail[]> {
  const all = await getAllPhotos();
  const query = options.query?.trim().toLowerCase() || '';
  const date = options.shotDate?.replaceAll('-', '.');

  return all
    .filter((photo) => {
      if (options.albumId && photo.albumId !== options.albumId) return false;
      if (options.favoriteOnly && !photo.isFavorite) return false;
      if (date && !photo.shotDate.startsWith(date)) return false;
      if (!query) return true;
      return [photo.caption, photo.originalFilename]
        .some((value) => value.toLowerCase().includes(query));
    })
    .sort((a, b) => b.shotDate.localeCompare(a.shotDate));
}

export async function getFavoritePhotos(): Promise<PhotoDetail[]> {
  const all = await getAllPhotos();
  return all.filter((photo) => photo.isFavorite);
}

export async function getLastYearTodayMemories(): Promise<{
  title: string;
  memoryDate: string;
  photos: PhotoDetail[];
}> {
  const now = new Date();
  const lastYearDate = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
  const memoryDate = `${lastYearDate.getFullYear()}.${String(lastYearDate.getMonth() + 1).padStart(2, '0')}.${String(lastYearDate.getDate()).padStart(2, '0')}`;
  const all = await getAllPhotos();
  return {
    title: '작년 오늘의 추억',
    memoryDate: memoryDate.replaceAll('.', '-'),
    photos: all.filter((photo) => photo.shotDate === memoryDate),
  };
}

export async function setPhotoFavorite(
  id: string,
  favorite: boolean,
): Promise<PhotoDetail | undefined> {
  if (AUTH_MODE !== 'mock') return undefined;
  const photos = ensureMeta();
  const index = photos.findIndex((photo) => photo.id === id);
  if (index === -1) return undefined;

  const updated = { ...photos[index], isFavorite: favorite };
  photos[index] = updated;
  writeMetaStorage(photos);
  return updated;
}

/** Add a new photo (blob + metadata) */
export async function addPhoto(photo: PhotoDetail, blob: Blob): Promise<PhotoDetail> {
  if (AUTH_MODE !== 'mock') return photo;

  // 1. Save blob to IndexedDB
  try {
    const db = await openDB();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_BLOB, 'readwrite');
      tx.objectStore(STORE_BLOB).put({ id: photo.id, blob });
      tx.oncomplete = () => { db.close(); resolve(); };
      tx.onerror = () => { db.close(); reject(tx.error); };
    });
  } catch {
    throw new Error('사진 저장에 실패했습니다. 저장 공간을 확인해주세요.');
  }

  // 2. Save metadata
  const photos = ensureMeta();
  photos.unshift(photo);
  _metaCache = photos;
  writeMetaStorage(photos);

  return photo;
}

export async function downloadPhotosAsZipBlob(ids: string[]): Promise<{ blob: Blob; totalSize: number; filename: string } | null> {
  // Dynamic import for JSZip
  const JSZip = (await import('jszip')).default;
  const zip = new JSZip();

  const nameCount: Record<string, number> = {};
  let totalSize = 0;

  for (const id of ids) {
    const result = await getPhotoBlob(id);
    if (!result) continue;

    let filename = result.meta.originalFilename || `photo_${id}.jpg`;
    // Handle duplicates
    if (nameCount[filename] !== undefined) {
      nameCount[filename]++;
      const dotIdx = filename.lastIndexOf('.');
      if (dotIdx > 0) {
        filename = `${filename.slice(0, dotIdx)}_${nameCount[filename]}${filename.slice(dotIdx)}`;
      } else {
        filename = `${filename}_${nameCount[filename]}`;
      }
    } else {
      nameCount[filename] = 0;
    }

    zip.file(filename, result.blob);
    totalSize += result.meta.fileSize;
  }

  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');

  const zipBlob = await zip.generateAsync({ type: 'blob' });
  return {
    blob: zipBlob,
    totalSize,
    filename: `가족앨범_${yyyy}${mm}${dd}.zip`,
  };
}
